import React, { useState, useEffect } from "react";
import {
  ArrowRight,
  Users,
  TrendingUp,
  ShieldCheck,
  Coffee,
  Target,
  Mail,
  GraduationCap,
  CheckCircle2,
} from "lucide-react";

const Career = () => {
  // --- LANGUAGE SYNC LOGIC ---
  const [lang, setLang] = useState(localStorage.getItem("lang") || "en");

  useEffect(() => {
    const handleStorageChange = () => {
      setLang(localStorage.getItem("lang") || "en");
    };
    window.addEventListener("storage", handleStorageChange);
    const interval = setInterval(handleStorageChange, 500);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const content = {
    en: {
      headline: "Do great work. With great people.",
      intro:
        "We are a firm with high standards and genuine ambition. The people who thrive here are those who care deeply about doing right by their clients, take pride in their craft, and want to grow in an environment that takes them seriously. If that sounds like you, we would very much like to hear from you.",

      whyTitle: "What makes this different",
      whyPoints: [
        "You will work closely with experienced advisors from day one, not spend months in a classroom.",
        "Your clients are real, your contribution is visible, and your growth is actively supported.",
        "We work with a carefully selected insurer panel, so you will develop genuine product depth, not just surface-level breadth.",
        "The culture here is warm, professional, and built on mutual respect.",
        "This is a place to build something, not just hold a position.",
      ],

      roleTitle: "Financial Advisor – Insurance",
      roleDesc:
        "We are looking for a motivated, client-focused individual to join our advisory team. You will manage a growing portfolio of individual and corporate clients, providing personalised insurance guidance across all lines and building the kind of long-term relationships that are the foundation of everything we do.",
      dayToDayTitle: "Day to day, you will:",
      dayToDay: [
        "Conduct thorough client assessments to understand their risk profile and coverage needs.",
        "Recommend and place appropriate insurance solutions from our insurer panel.",
        "Manage renewals, policy reviews, and ongoing client communications with care.",
        "Build your client base through referrals, relationships, and a reputation for excellence.",
        "Work closely with the wider team, sharing insights and supporting each other's growth.",
      ],
      fitTitle: "You will be a great fit if you:",
      fit: [
        "Have experience in financial advisory, insurance, or a client-facing professional services role.",
        "Communicate clearly, listen carefully, and earn trust naturally.",
        "Take pride in the quality of your work and hold yourself to a high standard.",
        "Are energised by building relationships, not just completing transactions.",
        "Want to grow in a firm that will invest in your development and reward your performance.",
      ],

      newTitle: "No experience? We are open.",
      newDesc:
        "We welcome candidates from all walks of life. If you are a career changer or recent graduate with the right mindset and work ethic, we will give you the training, mentorship, and support to build a genuinely rewarding career here. What matters most to us is who you are, not only what you have done.",

      applyTitle: "How to apply",
      applySub:
        "Send your CV and a short note introducing yourself to bizops.jtgaby@rigors.com.sg. Tell us a little about why Rigors interests you. We read every application personally and will be in touch with those we would like to meet.",
      applyBtn: "Apply: bizops.jtgaby@rigors.com.sg",
    },
    zh: {
      headline: "做好卓越的工作，与优秀的人同行",
      intro:
        "我们是一家坚持高标准并具备明确发展愿景的公司。在这里能够脱颖而出的人，是真正关心客户、对自身专业充满责任感，并希望在一个重视其成长与价值的环境中不断进步的人。如果这正是您，我们非常期待与您交流。",

      whyTitle: "与众不同之处",
      whyPoints: [
        "从第一天起您就将与经验丰富的顾问紧密合作，而不是在教室里度过数月。",
        "您的客户是真实的，您的贡献是可见的，您的成长将得到积极支持。",
        "我们与精心挑选的保险公司合作，因此您将培养真正的产品深度，而不仅仅是表面的广度。",
        "这里的文化是温暖、专业且建立在相互尊重的基础上的。",
        "这是一个创造价值的地方，而不仅仅是一个职位。",
      ],

      roleTitle: "财务顾问 – 保险",
      roleDesc:
        "我们正在寻找一位积极进取、以客户为中心的优秀人才加入我们的顾问团队。您将管理不断增长的个人及企业客户组合，提供全方位的个性化保险指导，并建立长期稳定的客户关系——这是我们事业的基石。",
      dayToDayTitle: "在日常工作中，您将：",
      dayToDay: [
        "进行深入的客户评估，了解其风险状况和保障需求。",
        "从我们的保险合作伙伴中推荐并安排合适的保险方案。",
        "用心管理续保、保单检视及持续的客户沟通。",
        "通过转介绍、人际关系和卓越的声誉建立您的客户群。",
        "与团队紧密合作，分享见解并支持彼此的成长。",
      ],
      fitTitle: "如果您具备以下特质，将非常适合：",
      fit: [
        "拥有财务咨询、保险或面向客户的专业服务行业经验。",
        "沟通清晰，善于倾听，能自然地赢得他人信任。",
        "对工作质量感到自豪，并坚持高标准要求自己。",
        "热衷于建立人际关系，而非仅仅完成交易。",
        "希望在一家愿意投资您的发展并回报您的表现的公司成长。",
      ],

      newTitle: "没有经验？我们持开放态度。",
      newDesc:
        "我们欢迎来自各行各业的候选人。如果您是职场转型者或具备正确心态与职业道德的应届毕业生，我们将为您提供培训、指导和支持，助您在这里开启极具回报的职业生涯。对我们而言，最重要的是您是谁，而不仅仅是您做过什么。",

      applyTitle: "如何申请",
      applySub:
        "请将您的简历及一段简短的自我介绍发送至 bizops.jtgaby@rigors.com.sg，并说明您对 Rigors 感兴趣的原因。我们会认真阅读每一份申请，并与我们希望进一步沟通的候选人取得联系。",
      applyNote: "仅限直接邮件。无申请表。真诚且专注。",
      applyBtn: "申请: bizops.jtgaby@rigors.com.sg",
    },
  };

  const cur = content[lang];

  return (
    <div className="overflow-x-hidden transition-colors duration-500 bg-brand-bg text-brand-text font-arial">
    {/* 1 & 2. HEADLINE & INTRO */}
<section className="relative py-10 md:py-10  bg-white">
  <div className="container px-6 mx-auto max-w-7xl text-center">

    {/* HEADING */}
    <h1 className="text-[28px] md:text-[34px] font-[Playfair_Display] text-[#1A1A1A] leading-tight mb-4">
      {cur.headline}
    </h1>

    {/* PARAGRAPH */}
    <p className="text-[14px] md:text-[16px] mt-4 leading-relaxed text-[#999999] max-w-4xl mx-auto">
      {cur.intro}
    </p>

  </div>
</section>

     {/* 3. WHY RIGORS */}
<section className="py-10 md:py-10 bg-[#F5F0E8]">

  <div className="container px-6 mx-auto max-w-6xl">

    {/* HEADER */}
    <div className="text-center mb-12">

      <span className="text-[#B08D57] uppercase tracking-[0.4em] font-semibold text-[10px] mb-3 block">
        WHY RIGORS
      </span>

      <h2 className="text-[28px] md:text-[34px] font-[Playfair_Display] text-[#1A1A1A] leading-tight">
        {cur.whyTitle}
      </h2>

    </div>

    {/* GRID */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

      {cur.whyPoints.map((point, i) => (
        <div
          key={i}
          className="flex items-start gap-4 p-6 bg-white border border-[#E5E0D8] transition-all duration-300 hover:shadow-[0_10px_30px_rgba(0,0,0,0.05)]"
        >

         {/* ICON */}
<div className="w-10 h-10 rounded-full flex items-center justify-center bg-[#B08D57]/10 text-[#B08D57] shrink-0">
  <CheckCircle2 size={18} />
</div>

          {/* TEXT */}
          <p className="text-[14px] md:text-[16px] text-[#999999] leading-relaxed">
            {point}
          </p>

        </div>
      ))}

    </div>

  </div>
</section>

    {/* 4. OPEN ROLE */}
<section className="py-10 md:py-10 bg-white">
  <div className="container px-6 mx-auto max-w-7xl">

    {/* HEADER */}
    <div className="mb-10 text-center">
      <span className="text-[#B08D57] uppercase tracking-[0.4em] font-semibold text-[10px] mb-3 block">
        OPEN ROLE
      </span>

      <h2 className="text-[28px] md:text-[34px] mb-4 font-[Playfair_Display] text-[#1A1A1A]">
        {cur.roleTitle}
      </h2>

      <p className="text-[14px] md:text-[16px] text-[#999999] leading-relaxed max-w-5xl mx-auto">
        {cur.roleDesc}
      </p>
    </div>

    {/* CARDS */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">

      {/* LEFT CARD */}
      <div className="p-8 bg-[#999999]/10 border border-[#E5E0D8]">

        <h3 className="text-[18px] font-semibold text-[#1A1A1A] mb-6 flex items-center gap-4">

          {/* ICON */}
          <div className="w-12 h-12 rounded-full bg-[#B08D57]/10 flex items-center justify-center text-[#B08D57]">
            <i className="fa-solid fa-chart-line text-[16px]"></i>
          </div>

          {cur.dayToDayTitle}
        </h3>

        <div className="h-[1px] w-full bg-[#E5E0D8] mb-6"></div>

        {/* LIST */}
        <ul className="space-y-4">
          {cur.dayToDay.map((item, i) => (
            <li
              key={i}
              className="flex items-start gap-3 text-[#999999] text-[14px] md:text-[15px] leading-relaxed"
            >
              <i className="fa-solid fa-arrow-right text-[#B08D57] mt-[4px] text-[12px]"></i>
              <p>{item}</p>
            </li>
          ))}
        </ul>

      </div>

      {/* RIGHT CARD */}
      <div className="p-8 bg-[#999999]/10 border border-[#E5E0D8]">

        <h3 className="text-[18px] font-semibold text-[#1A1A1A] mb-6 flex items-center gap-4">

          {/* ICON */}
          <div className="w-12 h-12 rounded-full bg-[#B08D57]/10 flex items-center justify-center text-[#B08D57]">
            <i className="fa-solid fa-users text-[16px]"></i>
          </div>

          {cur.fitTitle}
        </h3>

        <div className="h-[1px] w-full bg-[#E5E0D8] mb-6"></div>

        {/* LIST */}
        <ul className="space-y-4">
          {cur.fit.map((item, i) => (
            <li
              key={i}
              className="flex items-start gap-3 text-[#999999] text-[14px] md:text-[15px] leading-relaxed"
            >
              <i className="fa-solid fa-arrow-right text-[#B08D57] mt-[4px] text-[12px]"></i>
              <p>{item}</p>
            </li>
          ))}
        </ul>

      </div>

    </div>

  </div>
</section>

     {/* 5. NEW TO THE INDUSTRY */}
<section className="py-4 md:py-8 bg-white">
  <div className="container px-6 mx-auto max-w-7xl">

    <div className="flex flex-col md:flex-row items-center gap-6 p-6 md:p-8 bg-white border border-[#E5E0D8]">

      {/* ICON */}
      <div className="shrink-0">
        <div className="w-14 h-14 rounded-full bg-[#B08D57]/10 flex items-center justify-center text-[#B08D57]">
          <i className="fa-solid fa-graduation-cap text-[20px]"></i>
        </div>
      </div>

      {/* CONTENT */}
      <div>
        <h2 className="text-xl md:text-2xl font-[Playfair_Display] text-[#1A1A1A] mb-2">
          {cur.newTitle}
        </h2>

        <p className="text-[14px] md:text-[16px] leading-relaxed text-[#999999] text-justify">
          {cur.newDesc}
        </p>
      </div>

    </div>

  </div>
</section>

    {/* 6. APPLICATION */}
<section className="relative py-10 md:py-10 bg-white">

  <div className="px-6 mx-auto max-w-6xl">

    {/* CREAM BOX */}
    <div className="bg-[#F5F0E8] border border-[#E5E0D8] p-8 md:p-8 lg:p-8 shadow-sm">

      <div className="flex flex-col gap-10 md:flex-row md:items-center md:justify-between">

        {/* LEFT */}
        <div className="w-full md:w-2/3 text-center md:text-left">

          {/* BADGE (optional small label) */}
          <span className="text-[#B08D57] uppercase tracking-[0.35em] text-[11px] mb-4 block">
            APPLY
          </span>

          {/* TITLE */}
          <h2 className="text-[28px] md:text-[34px] text-[#1A1A1A] leading-tight font-[Playfair_Display] mb-4">
            {cur.applyTitle}
          </h2>

          {/* TEXT */}
          <p className="text-[14px] md:text-[16px] text-[#999999] max-w-4xl leading-relaxed mx-auto md:mx-0">
            {cur.applySub}
          </p>

        </div>

        {/* RIGHT CTA */}
        <div className="flex justify-center md:justify-end md:w-1/3">

          <a
            href="mailto:bizops.jtgaby@rigors.com.sg"
            className="text-[#1A1A1A] text-[14px] font-semibold tracking-wide group flex items-center gap-2 hover:text-[#B08D57] transition"
          >
            Apply Now
            <span className="transition-transform duration-300 group-hover:translate-x-1">
              →
            </span>
          </a>

        </div>

      </div>

    </div>

  </div>

</section>
    </div>
  );
};

export default Career;
